<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';

const MINIMUM_MINUTES = 5;

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const minutes = ref(null);
const whenOffline = ref(false);
const action = ref(null);
const isEnabled = ref(false);
const isSubmitting = ref(false);

const actionOptions = computed(() => [
  {
    id: 'return_to_queue',
    name: t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.ACTION.RETURN_TO_QUEUE'),
  },
  {
    id: 'escalate_to_manager',
    name: t(
      'GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.ACTION.ESCALATE_TO_MANAGER'
    ),
  },
]);

watch(
  currentAccount,
  () => {
    const {
      inactivity_handoff_after,
      inactivity_handoff_action,
      inactivity_handoff_when_offline,
    } = currentAccount.value?.settings || {};

    minutes.value = inactivity_handoff_after;
    whenOffline.value = Boolean(inactivity_handoff_when_offline);
    action.value =
      actionOptions.value.find(
        option => option.id === inactivity_handoff_action
      ) ?? actionOptions.value[0];
    isEnabled.value = Boolean(
      inactivity_handoff_after || inactivity_handoff_when_offline
    );
  },
  { deep: true, immediate: true }
);

const updateSettings = async settings => {
  try {
    isSubmitting.value = true;
    await updateAccount(settings, { silent: true });
    useAlert(t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  if (minutes.value && minutes.value < MINIMUM_MINUTES) {
    useAlert(t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.MINUTES.ERROR'));
    return Promise.resolve();
  }

  if (!minutes.value && !whenOffline.value) {
    useAlert(t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.NO_RULE_ERROR'));
    return Promise.resolve();
  }

  return updateSettings({
    inactivity_handoff_after: minutes.value ? Number(minutes.value) : null,
    inactivity_handoff_when_offline: whenOffline.value,
    inactivity_handoff_action: action.value?.id,
  });
};

const toggleHandoff = async () => {
  if (isEnabled.value) return;

  minutes.value = null;
  whenOffline.value = false;
  await updateSettings({
    inactivity_handoff_after: null,
    inactivity_handoff_when_offline: false,
    inactivity_handoff_action: null,
  });
};
</script>

<template>
  <div
    class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <div class="flex justify-between items-center w-full">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.TITLE') }}
        </h3>
        <div class="flex justify-end">
          <Switch v-model="isEnabled" @change="toggleHandoff" />
        </div>
      </div>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.NOTE') }}
      </p>
    </div>

    <div v-if="isEnabled" class="px-5 py-4">
      <form class="grid gap-5" @submit.prevent="handleSubmit">
        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.MINUTES.LABEL')"
          :help-message="
            t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.MINUTES.HELP')
          "
        >
          <input
            v-model="minutes"
            type="number"
            :min="MINIMUM_MINUTES"
            max="1440"
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.OFFLINE.LABEL')"
          :help-message="
            t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.OFFLINE.HELP')
          "
        >
          <div
            class="flex justify-between items-center p-3 w-full h-12 text-sm rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-12"
          >
            <span>
              {{ t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.OFFLINE.TOGGLE') }}
            </span>
            <Switch v-model="whenOffline" />
          </div>
        </WithLabel>
        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.ACTION.LABEL')"
          :help-message="
            t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.ACTION.HELP')
          "
        >
          <SingleSelect
            v-model="action"
            :options="actionOptions"
            :placeholder="
              t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.ACTION.LABEL')
            "
            placeholder-icon="i-lucide-chevron-down"
            placeholder-trailing-icon
            variant="faded"
          />
        </WithLabel>
        <div class="flex gap-2">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="t('GENERAL_SETTINGS.FORM.INACTIVITY_HANDOFF.UPDATE_BUTTON')"
          />
        </div>
      </form>
    </div>
  </div>
</template>
